import { queueARMABeacon } from "@utils/ARMA";
import { showSignupErrorModalType } from "./ModalContext";
import { getErrorMessageType } from "@utils/useErrorMessage";

export const signupFormSubmit = async (
  email: string,
  showSuccessModal: () => void,
  showSignupErrorModal: showSignupErrorModalType,
  getErrorMessage: getErrorMessageType,
  callback?: () => void
) => {
  window.dataLayer = window.dataLayer || [];
  window.fp = window.fp || null;

  const data = new FormData();
  data.append("email", email);
  if (window.fp) {
    data.append("fingerprint", window.fp);
  }

  const RESEND_EMAIL = "RESEND_EMAIL";
  const CREATED_USER = "CREATED_USER";
  const USER_ALREADY_EXIST = "USER_ALREADY_EXIST";
  const MISSING_EMAIL = "MISSING_EMAIL";
  const INVALID_EMAIL = "INVALID_EMAIL";
  const DATABASE_CONFLICT = "DATABASE_CONFLICT";
  const INVALID_EMAIL_DEBOUNCE = "INVALID_EMAIL_DEBOUNCE";
  const FREE_EMAIL_DEBOUNCE = "FREE_EMAIL_DEBOUNCE";
  const UNKNOWN_ERROR = "UNKNOWN_ERROR";

  const handleEmailError = (code: string, errorMsg: string) => {
    window.dataLayer.push({
      event: "sign_up_error",
      error: errorMsg,
    });
    queueARMABeacon("trial", "submit_email_error", code);
    window.gtag?.("event", "sign_up_step_2_failed");
    showSignupErrorModal(
      "MODAL_ERROR",
      getErrorMessage("EMAIL_INVALID", "Invalid email address.")
    );
  };

  try {
    const response = await fetch("/api/actions/signup/", {
      method: "post",
      body: data,
    });

    if (response.status === 302) {
      const responseLocation = response.headers.get("location");
      if (responseLocation) {
        window.location.replace(responseLocation);
      } else {
        showSignupErrorModal(
          "MODAL_ERROR",
          getErrorMessage("SOMETHING_WENT_WRONG", "Something went wrong.")
        );
      }
      return;
    }

    const jsonResponse = await response.json();

    switch (jsonResponse.messages) {
      case RESEND_EMAIL:
        window.dataLayer.push({
          event: "sign_up_resend",
        });
        queueARMABeacon("trial", "submit_email_resend", email);
        window.gtag?.("event", "sign_up_step_2_resend");
        if (
          jsonResponse.extra &&
          jsonResponse.extra.startsWith("/user/register/redirect")
        ) {
          window.location.replace(
            "https://app.accuranker.com" + jsonResponse.extra
          );
        } else {
          showSuccessModal();
        }
        break;
      case CREATED_USER:
        window.dataLayer.push({
          event: "sign_up",
        });
        queueARMABeacon("trial", "submit_email", email);
        window.gtag?.("event", "sign_up_step_2");
        if (
          jsonResponse.extra &&
          jsonResponse.extra.startsWith("/user/register/redirect")
        ) {
          window.location.replace(
            "https://app.accuranker.com" + jsonResponse.extra
          );
        } else {
          showSuccessModal();
        }
        break;
      case USER_ALREADY_EXIST:
        window.dataLayer.push({
          event: "sign_up_error",
          error: jsonResponse.messages,
        });
        queueARMABeacon("trial", "submit_email_error", USER_ALREADY_EXIST);
        window.gtag?.("event", "sign_up_step_2_failed");
        showSignupErrorModal("ALREADY_EXIST_ERROR");
        break;
      case MISSING_EMAIL:
        handleEmailError(MISSING_EMAIL, jsonResponse.messages);
        break;
      case INVALID_EMAIL:
        handleEmailError(INVALID_EMAIL, jsonResponse.messages);
        break;
      case DATABASE_CONFLICT:
        handleEmailError(DATABASE_CONFLICT, jsonResponse.messages);
        break;
      case INVALID_EMAIL_DEBOUNCE:
        window.dataLayer.push({
          event: "sign_up_error",
          error: jsonResponse.messages,
        });
        queueARMABeacon("trial", "submit_email_error", INVALID_EMAIL_DEBOUNCE);
        window.gtag?.("event", "sign_up_step_2_failed");
        showSignupErrorModal(
          "MODAL_ERROR",
          getErrorMessage(
            "EMAIL_VALIDATION_PROBLEMS",
            "We were unable to validate your email. Please enter another email address."
          )
        );
        break;
      case FREE_EMAIL_DEBOUNCE:
        window.dataLayer.push({
          event: "sign_up_error",
          error: jsonResponse.messages,
        });
        window.dataLayer.push({
          event: "work-email-modal-shown",
        });
        queueARMABeacon("trial", "submit_email_error", FREE_EMAIL_DEBOUNCE);
        window.gtag?.("event", "sign_up_step_2_failed");
        showSignupErrorModal("WORK_EMAIL_ERROR");
        break;
      default:
        window.dataLayer.push({
          event: "sign_up_error",
          error: UNKNOWN_ERROR,
        });
        queueARMABeacon("trial", "submit_email_error", UNKNOWN_ERROR);
        window.gtag?.("event", "sign_up_step_2_failed");
        showSignupErrorModal(
          "MODAL_ERROR",
          getErrorMessage("SOMETHING_WENT_WRONG", "Something went wrong.")
        );
    }
  } catch (err) {
    // Handle network error or other async errors.
    queueARMABeacon("trial", "submit_email_error", "FETCH_REQUEST_FAILED");
    window.gtag?.("event", "sign_up_step_2_failed");
    showSignupErrorModal(
      "MODAL_ERROR",
      getErrorMessage(
        "NETWORK_ERROR",
        "Network error or unable to reach the server."
      )
    );
  }
  if (callback) callback();
};
